import { normalizeFirstName, normalizeLastName } from '@massilia/features'
import { Button, cn, Name } from '@massilia/ui'
import { Calendar, Check, Pencil } from 'lucide-react'
import { type ReactNode, useState } from 'react'

import type { ReservationPassengerSchema } from '@/lib/schemas/reservation/reservation.schema'

import { formatDateLong } from '@/utils/format-date'

import { GENDER_COLORS, GENDER_LABELS } from '../-utils/constants'

interface PassengerCardProps {
  children: ReactNode
  /** Form fields for editing firstName, lastName, and birthDate — rendered only in edit mode */
  editFields: ReactNode
  /**
   * Ce qui empêche ce voyageur de passer la validation, le cas échéant.
   *
   * Sans ça, l'échec était **muet** : les champs de nom ne sont montés qu'en
   * mode édition, donc un nom refusé — un accent, une apostrophe, que le GDS ne
   * sait pas traiter — n'avait nulle part où s'afficher. Le client cliquait
   * « Confirmer ma réservation » et il ne se passait rien du tout.
   */
  errorMessage?: null | string
  index: number
  passenger: ReservationPassengerSchema
}

export function PassengerCard({
  children,
  editFields,
  errorMessage,
  index,
  passenger,
}: PassengerCardProps) {
  const [isEditing, setIsEditing] = useState(false)
  // Une carte en erreur s'ouvre d'office : le champ fautif doit être sous les
  // yeux du client, pas derrière un bouton « Modifier » qu'il n'a aucune raison
  // de presser.
  const isExpanded = isEditing || Boolean(errorMessage)
  const initials = getInitials(passenger.firstName, passenger.lastName)
  const genderColor = GENDER_COLORS[passenger.gender] || GENDER_COLORS.MALE

  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-xl border transition-all duration-300',
        'bg-white hover:shadow-md',
        errorMessage && 'border-red-300 bg-red-50/40'
      )}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="p-4">
        {/* Header with avatar and basic info */}
        <div className="mb-4 flex items-start gap-3">
          {/* Avatar */}
          <div
            className={cn(
              'flex h-12 w-12 shrink-0 items-center justify-center rounded-lg',
              'bg-linear-to-br shadow-sm',
              'transition-transform duration-300 group-hover:scale-105',
              genderColor
            )}
          >
            <span className="text-sm font-bold text-white">{initials}</span>
          </div>

          {/* Name and details */}
          <div className="min-w-0 flex-1">
            <div className="mb-0.5 text-xs font-medium tracking-wider text-stone-400 uppercase">
              Passager {index + 1}
            </div>

            {isExpanded ? (
              <div className="mt-1">{editFields}</div>
            ) : (
              <>
                {/* Jamais tronqué : c'est le nom qui doit correspondre au
                    passeport, c'est précisément ce que le client vient
                    vérifier ici. */}
                <h4 className="font-semibold break-words text-stone-800">
                  {GENDER_LABELS.get(passenger.gender)}{' '}
                  <Name>
                    {normalizeFirstName(passenger.firstName)}{' '}
                    {normalizeLastName(passenger.lastName)}
                  </Name>
                </h4>
                <div className="mt-1 flex items-center gap-1.5 text-sm text-stone-500">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>{formatDateLong(passenger.birthDate)}</span>
                </div>
              </>
            )}
          </div>

          {/* Edit / Done button */}
          <Button
            className="h-7 shrink-0 gap-1 text-xs text-blue-600 hover:text-blue-800"
            onClick={() => setIsEditing((v) => !v)}
            size="sm"
            type="button"
            variant="ghost"
          >
            {isExpanded ? (
              <>
                <Check className="size-3.5" />
                Valider
              </>
            ) : (
              <>
                <Pencil className="size-3.5" />
                Modifier
              </>
            )}
          </Button>
        </div>

        {errorMessage && (
          <p className="mb-4 text-sm font-medium text-red-700" role="alert">
            {errorMessage}
          </p>
        )}

        {/* Divider */}
        <div className="mb-4 border-t border-dashed border-stone-200" />

        {/* Editable fields - rendered via children */}
        {children}
      </div>
    </div>
  )
}

function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
}
